import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth, buildPublicProfile, uidToHue } from '../contexts/AuthContext';
import type {
  PublicProfile,
  SangatiAssistantResponse,
  SangatiHandshake,
  SangatiMember,
  SangatiMessage,
  SangatiThreadDetail,
  SangatiThreadSummary,
} from '../lib/api';
import {
  approveSangatiHandshake,
  askSangatiAssistant,
  createSangatiHandshake,
  createSangatiThread,
  getSangatiMembers,
  getSangatiHandshakes,
  getSangatiThread,
  getSangatiThreads,
  postSangatiThreadMessage,
} from '../lib/api';
import { useGlossary } from '../contexts/GlossaryContext';
import { calculateGatewayProgress } from '../lib/gatewayPolicy';
import { VEDIC_DIMENSIONS } from '../lib/vedicDimensions';
import {
  createSangatiGroup,
  loadSangatiGroup,
  removeGroupMember,
  saveSangatiGroup,
  upsertGroupMember,
  type SangatiGroup,
} from '../lib/sangati';
import '../styles/SangatiPage.css';

interface SangatiPageProps {
  onNavigate: (path: string) => void;
  onNavigateHome: () => void;
  onNavigateTutor: () => void;
}

type RoleFilter = 'all' | 'learner' | 'knowledge_provider' | 'both';

type TranslateFn = ReturnType<typeof useGlossary>['t'];

function roleLabel(kind?: string, t?: TranslateFn) {
  if (kind === 'knowledge_provider') return t ? t('profile.kind.provider') : '🪔 ज्ञान प्रदाता';
  if (kind === 'both') return t ? t('profile.kind.both') : '🔁 द्विविध भूमिका';
  return t ? t('profile.kind.learner') : '🧑‍🎓 शिक्षार्थी';
}

type RoadmapStep = {
  key: string;
  title: string;
  subtitle: string;
  path?: string;
  actionLabel?: string;
  secondary?: { label: string; path: string };
  badge: string;
  note?: string;
  soon?: boolean;
};

function SangatiRoadmap({ onNavigate }: { onNavigate: (path: string) => void; }) {
  const { t } = useGlossary();
  const steps: RoadmapStep[] = [
    {
      key: 'deeksha',
      title: t('sangati.roadmap.milestones.deeksha'),
      subtitle: t('sangati.roadmap.startSubtitle'),
      path: '/curriculum#slide-001',
      actionLabel: t('sangati.roadmap.stepOpen'),
      secondary: { label: t('sangati.roadmap.primaryAction'), path: '/journey' },
      badge: t('sangati.roadmap.startBadge'),
      note: t('sangati.roadmap.startNote'),
    },
    {
      key: 'vad',
      title: t('sangati.roadmap.milestones.vad'),
      subtitle: t('sangati.roadmap.vadSubtitle'),
      path: '/journey',
      actionLabel: t('sangati.roadmap.primaryAction'),
      badge: t('sangati.roadmap.vadBadge'),
      secondary: { label: t('sangati.nextStep.openStudio'), path: '/studio' },
      note: t('sangati.roadmap.vadNote'),
    },
    {
      key: 'mitrata',
      title: t('sangati.roadmap.milestones.mitrata'),
      subtitle: t('sangati.roadmap.mitrataSubtitle'),
      badge: t('sangati.roadmap.comingSoonBadge'),
      soon: true,
      note: t('sangati.roadmap.comingSoonNote'),
    },
    {
      key: 'samvaad',
      title: `${t('sangati.roadmap.practiceLabel')} · ${t('sangati.roadmap.milestones.samvaad')}`,
      subtitle: t('sangati.roadmap.samvaadSubtitle'),
      path: '/curriculum',
      actionLabel: t('sangati.roadmap.samvaadAction'),
      badge: t('sangati.roadmap.practiceBadge'),
      note: t('sangati.roadmap.practiceNote'),
    },
    {
      key: 'kartavyabhyas',
      title: t('sangati.roadmap.milestones.kartavyabhyas'),
      subtitle: t('sangati.roadmap.kartavyabhyasSubtitle'),
      badge: t('sangati.roadmap.comingSoonBadge'),
      soon: true,
      note: t('sangati.roadmap.kartavyabhyasNote'),
    },
    {
      key: 'vritti',
      title: `${t('sangati.roadmap.milestones.vritti')} · ${t('sangati.roadmap.vrittiLabel')}`,
      subtitle: t('sangati.roadmap.vrittiSubtitle'),
      path: '/careers',
      actionLabel: t('sangati.roadmap.vrittiAction'),
      badge: t('sangati.roadmap.vrittiBadge'),
      note: t('sangati.roadmap.vrittiNote'),
    },
  ];

  const roadStops = [10, 28, 46, 64, 82, 94];
  const roadMilestones = [
    { x: 10, label: t('sangati.roadmap.milestones.deeksha') },
    { x: 28, label: t('sangati.roadmap.milestones.vad') },
    { x: 46, label: t('sangati.roadmap.milestones.mitrata') },
    { x: 64, label: t('sangati.roadmap.milestones.samvaad') },
    { x: 82, label: t('sangati.roadmap.milestones.kartavyabhyas') },
    { x: 94, label: t('sangati.roadmap.milestones.vritti') },
  ];

  return (
    <section className="sangati-roadmap" aria-labelledby="sangati-roadmap-title">
      <div className="sangati-roadmap-head">
        <div>
          <div className="sangati-roadmap-eyebrow">{t('sangati.roadmap.eyebrow')}</div>
          <h2 id="sangati-roadmap-title">{t('sangati.roadmap.title')}</h2>
          <p>
            {t('sangati.roadmap.summary')}
          </p>
        </div>
        <button type="button" className="sangati-btn sangati-btn--primary" onClick={() => onNavigate('/journey')}>
          {t('sangati.roadmap.primaryAction')}
        </button>
      </div>

      <div className="sangati-roadmap-svg-wrap">
        <svg
          viewBox="0 0 1000 180"
          role="img"
          aria-labelledby="sangati-roadmap-svg-title sangati-roadmap-svg-desc"
          focusable="false"
          className="sangati-roadmap-svg"
        >
          <title id="sangati-roadmap-svg-title">{t('sangati.roadmap.handbookTitle')}</title>
          <desc id="sangati-roadmap-svg-desc">
            {t('sangati.roadmap.handbookDesc')}
          </desc>
          <defs>
            <linearGradient id="road-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#8b4513" />
              <stop offset="100%" stopColor="#d4af37" />
            </linearGradient>
            <marker id="road-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="#8b4513" />
            </marker>
          </defs>
          <path
            d="M 60 82 C 150 28, 220 28, 300 82 S 450 136, 530 82 S 680 28, 760 82 S 900 136, 940 82"
            fill="none"
            stroke="url(#road-gradient)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="0"
            markerEnd="url(#road-arrow)"
          />
          <path
            d="M 940 82 C 870 132, 780 136, 690 110 C 600 86, 540 52, 470 52 C 380 52, 300 110, 210 110 C 140 110, 95 100, 60 82"
            fill="none"
            stroke="rgba(139, 69, 19, 0.18)"
            strokeWidth="4"
            strokeDasharray="10 12"
          />
          {roadStops.map((x, index) => (
            <g key={x}>
              <circle cx={x * 10} cy={82} r="16" fill="#fffaf4" stroke="#8b4513" strokeWidth="4" />
              <circle cx={x * 10} cy={82} r="7" fill={index === 0 ? '#d4af37' : index === roadStops.length - 1 ? '#8b4513' : '#c9a43a'} />
            </g>
          ))}
          {roadMilestones.map((milestone, index) => (
            <g key={milestone.label}>
              <line
                x1={milestone.x * 10}
                y1={index % 2 === 0 ? 58 : 126}
                x2={milestone.x * 10}
                y2={82}
                stroke="rgba(139, 69, 19, 0.35)"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
              <text
                x={milestone.x * 10}
                y={index % 2 === 0 ? 42 : 150}
                textAnchor="middle"
                className="sangati-roadmap-svg-label"
              >
                {milestone.label}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <div className="sangati-roadmap-grid">
        {steps.map((step) => (
          <article
            key={step.key}
            className={`sangati-roadmap-card ${step.soon ? 'sangati-roadmap-card--soon' : ''}`}
          >
            <div className="sangati-roadmap-card-head">
              <span className="sangati-roadmap-step-index">{step.badge}</span>
              <span className="sangati-roadmap-step-loop">↺</span>
            </div>
            <h3>{step.title}</h3>
            <p>{step.subtitle}</p>
            {step.note && <div className="sangati-roadmap-note">{step.note}</div>}
            <div className="sangati-roadmap-actions">
              {step.path ? (
                <button type="button" className="sangati-roadmap-btn" onClick={() => onNavigate(step.path!)}>
                  {step.actionLabel ?? 'Open'}
                </button>
              ) : (
                <span className="sangati-roadmap-coming">{t('sangati.roadmap.stepComingSoon')}</span>
              )}
              {step.secondary && (
                <button type="button" className="sangati-roadmap-btn sangati-roadmap-btn--secondary" onClick={() => onNavigate(step.secondary!.path)}>
                  {step.secondary.label}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <div className="sangati-roadmap-foot">{t('sangati.roadmap.loopFoot')}</div>
      <section className="sangati-next-step">
        <div>
          <div className="sangati-next-step-eyebrow">{t('sangati.nextStep.eyebrow')}</div>
          <h3>{t('sangati.nextStep.title')}</h3>
          <p>{t('sangati.nextStep.body')}</p>
        </div>
        <div className="sangati-next-step-visual">
          <svg viewBox="0 0 560 110" role="img" aria-label={t('sangati.roadmap.handbookTitle')}>
            <path d="M40 55 C120 12, 190 12, 250 55 S 380 98, 440 55 S 500 30, 520 55" fill="none" stroke="url(#sangati-vad-gradient)" strokeWidth="10" strokeLinecap="round" />
            <defs>
              <linearGradient id="sangati-vad-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8b4513" />
                <stop offset="100%" stopColor="#d4af37" />
              </linearGradient>
            </defs>
            <circle cx="40" cy="55" r="15" fill="#fffaf4" stroke="#8b4513" strokeWidth="4" />
            <circle cx="520" cy="55" r="15" fill="#fffaf4" stroke="#8b4513" strokeWidth="4" />
            <text x="40" y="86" textAnchor="middle">{t('sangati.eyebrow')}</text>
            <text x="520" y="86" textAnchor="middle">{t('sangati.roadmap.primaryAction')}</text>
          </svg>
        </div>
        <div className="sangati-next-step-actions">
          <button type="button" className="sangati-roadmap-btn" onClick={() => onNavigate('/journey')}>
            {t('sangati.nextStep.openVad')}
          </button>
          <button type="button" className="sangati-roadmap-btn sangati-roadmap-btn--secondary" onClick={() => onNavigate('/studio')}>
            {t('sangati.nextStep.openStudio')}
          </button>
        </div>
      </section>
    </section>
  );
}

function matchesPincode(member: PublicProfile, query: string): boolean {
  if (!query) return true;
  return String(member.pincode ?? '').startsWith(query);
}

function matchesAge(member: PublicProfile, minAge: string, maxAge: string): boolean {
  if (!minAge && !maxAge) return true;
  if (typeof member.age !== 'number') return false;
  if (minAge && member.age < Number(minAge)) return false;
  if (maxAge && member.age > Number(maxAge)) return false;
  return true;
}

function matchesRating(member: PublicProfile, minRating: string): boolean {
  if (!minRating) return true;
  if (typeof member.rating !== 'number') return false;
  return member.rating >= Number(minRating);
}

function buildThreadKey(type: 'group' | 'dm', participantUids: string[]): string {
  return `${type}:${Array.from(new Set(participantUids.map((uid) => uid.trim()).filter(Boolean))).sort().join('|')}`;
}

function formatTimestamp(value: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeLookupCode(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

export default function SangatiPage({ onNavigate, onNavigateHome, onNavigateTutor }: SangatiPageProps) {
  const { t } = useGlossary();
  const { profile, user, policy } = useAuth();
  const [members, setMembers] = useState<SangatiMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [group, setGroup] = useState<SangatiGroup | null>(null);
  const [threads, setThreads] = useState<SangatiThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState('');
  const [activeThreadId, setActiveThreadId] = useState('');
  const [activeThread, setActiveThread] = useState<SangatiThreadDetail | null>(null);
  const [activeThreadLoading, setActiveThreadLoading] = useState(false);
  const [activeThreadError, setActiveThreadError] = useState('');
  const [composer, setComposer] = useState('');
  const [assistantLog, setAssistantLog] = useState<Array<{ role: 'user' | 'assistant'; text: string }>>([
    {
      role: 'assistant',
      text: t('sangati.assistant.welcome'),
    },
  ]);
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState('');
  const [assistantProvider, setAssistantProvider] = useState('');
  const [assistantPrompts, setAssistantPrompts] = useState<string[]>([
    t('sangati.assistant.prompt1'),
    t('sangati.assistant.prompt2'),
    t('sangati.assistant.prompt3'),
  ]);
  const [handshakeLookupCode, setHandshakeLookupCode] = useState('');
  const [handshakeInviteeEmail, setHandshakeInviteeEmail] = useState('');
  const [handshakeTarget, setHandshakeTarget] = useState<{ uid: string; alias: string; code: string } | null>(null);
  const [handshakeLoading, setHandshakeLoading] = useState(false);
  const [handshakeError, setHandshakeError] = useState('');
  const [handshakeRequests, setHandshakeRequests] = useState<SangatiHandshake[]>([]);
  const gatewayProgress = profile ? calculateGatewayProgress(profile, policy) : null;
  const onboardingBanner = new URLSearchParams(window.location.search).has('onboarding');

  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [minRating, setMinRating] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getSangatiMembers()
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch((err) => {
        console.error('Failed to load Sangati members:', err);
        if (!cancelled) setError(t('sangati.error.load'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profile) {
      setGroup(null);
      return;
    }
    setGroup(loadSangatiGroup(profile.uid));
  }, [profile?.uid]);

  useEffect(() => {
    if (!profile) {
      setThreads([]);
      setActiveThreadId('');
      setActiveThread(null);
      return;
    }

    let cancelled = false;
    setThreadsLoading(true);
    setThreadsError('');
    getSangatiThreads(profile.uid)
      .then((data) => {
        if (cancelled) return;
        setThreads(data);
        setActiveThreadId((current) => {
          if (current && data.some((thread) => thread.id === current)) return current;
          return data[0]?.id ?? '';
        });
      })
      .catch((err) => {
        console.error('Failed to load Sangati threads:', err);
        if (!cancelled) setThreadsError(t('sangati.chat.error.loadThreads'));
      })
      .finally(() => {
        if (!cancelled) setThreadsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profile?.uid]);

  useEffect(() => {
    if (!profile || !activeThreadId) {
      setActiveThread(null);
      return;
    }

    let cancelled = false;
    setActiveThreadLoading(true);
    setActiveThreadError('');
    getSangatiThread(activeThreadId, profile.uid)
      .then((data) => {
        if (!cancelled) setActiveThread(data);
      })
      .catch((err) => {
        console.error('Failed to load Sangati thread:', err);
        if (!cancelled) {
          setActiveThread(null);
          setActiveThreadError(t('sangati.chat.error.loadThread'));
        }
      })
      .finally(() => {
        if (!cancelled) setActiveThreadLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profile?.uid, activeThreadId]);

  useEffect(() => {
    if (!user?.email) {
      setHandshakeRequests([]);
      return;
    }

    let cancelled = false;
    getSangatiHandshakes(user.email)
      .then((data) => {
        if (!cancelled) setHandshakeRequests(data);
      })
      .catch((err) => {
        console.error('Failed to load Sangati handshakes:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  const currentMember = useMemo<SangatiMember | null>(() => {
    if (!profile) return null;
    return {
      ...buildPublicProfile(profile),
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
      isDiscoverable: profile.isDiscoverable,
    };
  }, [profile]);

  const allMembers = useMemo(() => {
    const combined = [...members];
    if (currentMember && !combined.some((member) => member.uid === currentMember.uid)) {
      combined.unshift(currentMember);
    }
    return combined;
  }, [members, currentMember]);

  const memberLookup = useMemo(() => new Map(allMembers.map((member) => [member.uid, member])), [allMembers]);

  const visibleMembers = useMemo(() => {
    const uniqueMembers = allMembers.filter((member, index, all) => all.findIndex((item) => item.uid === member.uid) === index);
    return uniqueMembers.filter((member) => {
      if (roleFilter !== 'all' && member.profileKind !== roleFilter) return false;
      if (!matchesPincode(member, pincodeFilter.trim())) return false;
      if (!matchesAge(member, minAge, maxAge)) return false;
      if (!matchesRating(member, minRating)) return false;
      return true;
    });
  }, [allMembers, roleFilter, pincodeFilter, minAge, maxAge, minRating]);

  const groupMembers = useMemo(() => {
    if (!group) return [];
    return allMembers.filter((member) => group.memberUids.includes(member.uid));
  }, [group, allMembers]);

  const visibleGroupMembers = useMemo(() => {
    if (!group) return [];
    return groupMembers.filter((member, index, all) => all.findIndex((item) => item.uid === member.uid) === index);
  }, [group, groupMembers]);

  const activeThreadParticipants = useMemo(() => {
    if (!activeThread) return [];
    return activeThread.thread.participant_uids
      .map((uid) => memberLookup.get(uid)?.alias || uid)
      .filter(Boolean);
  }, [activeThread, memberLookup]);

  const groupThreadExists = useMemo(() => {
    if (!group || visibleGroupMembers.length < 2) return false;
    const key = buildThreadKey('group', visibleGroupMembers.map((member) => member.uid));
    return threads.some((thread) => thread.type === 'group' && buildThreadKey('group', thread.participant_uids) === key);
  }, [group, threads, visibleGroupMembers]);

  useEffect(() => {
    if (!profile || !group || visibleGroupMembers.length < 2 || threads.length === 0) return;
    const targetKey = buildThreadKey('group', visibleGroupMembers.map((member) => member.uid));
    const match = threads.find((thread) => thread.type === 'group' && buildThreadKey('group', thread.participant_uids) === targetKey);
    if (match && match.id !== activeThreadId) {
      setActiveThreadId(match.id);
    }
  }, [profile?.uid, visibleGroupMembers, threads, activeThreadId]);

  const ensureGroup = (): SangatiGroup | null => {
    if (!profile) {
      setToast(t('sangati.toast.signIn'));
      return null;
    }
    const existing = group ?? createSangatiGroup(profile);
    if (!group) {
      saveSangatiGroup(existing);
      setGroup(existing);
    }
    return existing;
  };

  const refreshThreads = async (focusThreadId?: string) => {
    if (!profile) return;
    const nextThreads = await getSangatiThreads(profile.uid);
    setThreads(nextThreads);
    setActiveThreadId((current) => {
      if (focusThreadId) return focusThreadId;
      if (current && nextThreads.some((thread) => thread.id === current)) return current;
      return nextThreads[0]?.id ?? '';
    });
  };

  const handleCreateGroup = () => {
    if (!profile) {
      onNavigateTutor();
      return;
    }
    const next = createSangatiGroup(profile);
    saveSangatiGroup(next);
    setGroup(next);
    setToast(t('sangati.toast.created'));
  };

  const handleAddMember = (member: SangatiMember) => {
    const current = ensureGroup();
    if (!current) return;
    const next = upsertGroupMember(current, member.uid);
    saveSangatiGroup(next);
    setGroup(next);
    setToast(t('sangati.toast.memberAdded', { name: member.alias || t('sangati.unknown') }));
  };

  const handleAddMemberByUid = (uid: string) => {
    const member = memberLookup.get(uid);
    if (!member) {
      setToast(t('sangati.handshake.error.missingMember'));
      return;
    }
    handleAddMember(member);
  };

  const handleRemoveMember = (memberUid: string) => {
    if (!group) return;
    const next = removeGroupMember(group, memberUid);
    saveSangatiGroup(next);
    setGroup(next);
  };

  const handleOpenGroupChat = async () => {
    if (!profile || !group) {
      setToast(t('sangati.toast.signIn'));
      return;
    }
    if (group.memberUids.length < 2) {
      setToast(t('sangati.chat.groupNeedsMembers'));
      return;
    }
    try {
      const thread = await createSangatiThread({
        actor_uid: profile.uid,
        actor_alias: profile.alias,
        type: 'group',
        title: group.name,
        participant_uids: visibleGroupMembers.map((member) => member.uid),
      });
      await refreshThreads(thread.id);
      setToast(t('sangati.chat.groupOpened'));
    } catch (err) {
      console.error('Failed to open Sangati group thread:', err);
      setToast(t('sangati.chat.error.createThread'));
    }
  };

  const handleOpenDirectMessage = async (member: SangatiMember) => {
    if (!profile) {
      setToast(t('sangati.toast.signIn'));
      return;
    }
    if (!group || !group.memberUids.includes(member.uid)) {
      setToast(t('sangati.chat.dmNeedsMembership'));
      return;
    }
    try {
      const thread = await createSangatiThread({
        actor_uid: profile.uid,
        actor_alias: profile.alias,
        type: 'dm',
        title: member.alias ? `${member.alias} · ${t('sangati.chat.dmTitle')}` : t('sangati.chat.dmTitle'),
        participant_uids: [member.uid],
      });
      await refreshThreads(thread.id);
      setToast(t('sangati.chat.dmOpened', { name: member.alias || t('sangati.unknown') }));
    } catch (err) {
      console.error('Failed to open Sangati DM thread:', err);
      setToast(t('sangati.chat.error.createThread'));
    }
  };

  const handleLookupHandshakeCode = async (e: FormEvent) => {
    e.preventDefault();
    const code = normalizeLookupCode(handshakeLookupCode);
    if (!code) {
      setHandshakeError(t('sangati.handshake.error.lookup'));
      return;
    }

    setHandshakeLoading(true);
    setHandshakeError('');
    try {
      const matchedMember = allMembers.find((member) => {
        const memberCode = normalizeLookupCode(member.uid);
        const memberAlias = normalizeLookupCode(member.alias || '');
        return memberCode === code || memberAlias === code;
      });

      if (!matchedMember) {
        throw new Error(t('sangati.handshake.error.lookup'));
      }

      setHandshakeTarget({ uid: matchedMember.uid, alias: matchedMember.alias || matchedMember.uid, code: matchedMember.uid });
      setHandshakeInviteeEmail('');
    } catch (err) {
      console.error('Handshake lookup failed:', err);
      setHandshakeError(err instanceof Error ? err.message : t('sangati.handshake.error.lookup'));
      setHandshakeTarget(null);
    } finally {
      setHandshakeLoading(false);
    }
  };

  const handleUseMemberCode = (member: SangatiMember) => {
    setHandshakeLookupCode(member.uid);
    setHandshakeTarget({ uid: member.uid, alias: member.alias || member.uid, code: member.uid });
    setHandshakeError('');
  };

  const handleCreateHandshake = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile || !user?.email || !handshakeTarget) {
      setHandshakeError(t('sangati.handshake.error.signIn'));
      return;
    }
    const inviteeEmail = handshakeInviteeEmail.trim();
    if (!inviteeEmail) {
      setHandshakeError(t('sangati.handshake.error.email'));
      return;
    }

    setHandshakeLoading(true);
    setHandshakeError('');
    try {
      await createSangatiHandshake({
        requester_uid: profile.uid,
        requester_alias: profile.alias,
        requester_email: user.email,
        lookup_code: handshakeTarget.code,
        target_uid: handshakeTarget.uid,
        target_alias: handshakeTarget.alias,
        invitee_email: inviteeEmail,
      });
      setToast(t('sangati.handshake.created'));
      setHandshakeLookupCode('');
      setHandshakeTarget(null);
      setHandshakeInviteeEmail('');
      const next = await getSangatiHandshakes(user.email);
      setHandshakeRequests(next);
    } catch (err) {
      console.error('Handshake create failed:', err);
      setHandshakeError(t('sangati.handshake.error.create'));
    } finally {
      setHandshakeLoading(false);
    }
  };

  const handleApproveHandshake = async (request: SangatiHandshake) => {
    if (!profile || !user?.email) {
      setHandshakeError(t('sangati.handshake.error.signIn'));
      return;
    }
    try {
      const approved = await approveSangatiHandshake(request.code, {
        approver_uid: profile.uid,
        approver_alias: profile.alias,
        approver_email: user.email,
      });
      setHandshakeRequests((current) => current.map((item) => (item.code === approved.code ? approved : item)));
      if (approved.target_uid === profile.uid) {
        setToast(t('sangati.handshake.approved'));
      }
    } catch (err) {
      console.error('Handshake approval failed:', err);
      setHandshakeError(t('sangati.handshake.error.approve'));
    }
  };

  const handleSelectThread = (threadId: string) => {
    setActiveThreadId(threadId);
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile || !activeThreadId) return;
    const body = composer.trim();
    if (!body) return;
    try {
      const detail = await postSangatiThreadMessage(activeThreadId, {
        actor_uid: profile.uid,
        actor_alias: profile.alias,
        body,
      });
      setComposer('');
      setActiveThread(detail);
      await refreshThreads(activeThreadId);
    } catch (err) {
      console.error('Failed to send Sangati message:', err);
      setActiveThreadError(t('sangati.chat.error.sendMessage'));
    }
  };

  const sendAssistantPrompt = async (question: string) => {
    if (!profile) {
      setAssistantError(t('sangati.chat.error.signIn'));
      return;
    }
    const trimmed = question.trim();
    if (!trimmed) return;

    setAssistantLog((current) => [...current, { role: 'user', text: trimmed }]);
    setAssistantInput('');
    setAssistantLoading(true);
    setAssistantError('');

    try {
      const response: SangatiAssistantResponse = await askSangatiAssistant({
        question: trimmed,
        actor_uid: profile.uid,
        actor_alias: profile.alias,
        feature: 'sangati',
      });
      setAssistantProvider(response.provider);
      setAssistantPrompts(response.suggestions.length > 0 ? response.suggestions : assistantPrompts);
      setAssistantLog((current) => [...current, { role: 'assistant', text: response.answer }]);
    } catch (err) {
      console.error('Sangati assistant failed:', err);
      const message = t('sangati.assistant.error');
      setAssistantError(message);
      setAssistantLog((current) => [...current, { role: 'assistant', text: message }]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleAssistantSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await sendAssistantPrompt(assistantInput);
  };

  return (
    <div className="sangati-page">
      {(onboardingBanner || gatewayProgress) && (
        <section className="sangati-panel" aria-label="Onboarding summary">
          <div className="sangati-panel-title">Initial Adhikara summary</div>
          <div className="sangati-group-meta">
            <strong>{gatewayProgress?.stageKey ? t(`profileGateway.stage.${gatewayProgress.stageKey}`) : 'Awaiting stage'}</strong>
            <span>{gatewayProgress ? `Score ${gatewayProgress.adhikaraScore ?? '—'} · ${gatewayProgress.percent}%` : 'No profile progress yet'}</span>
          </div>
          <div className="sangati-adhikara-meter">
            <div className="sangati-adhikara-meter-fill" style={{ width: `${gatewayProgress?.percent ?? 0}%` }} />
          </div>
          <div className="sangati-botanical">
            <span className="sangati-botanical-icon">
              {gatewayProgress?.stageKey === 'seed' ? '🌱'
                : gatewayProgress?.stageKey === 'sprout' ? '🌿'
                  : gatewayProgress?.stageKey === 'leaf' ? '🍃'
                    : gatewayProgress?.stageKey === 'bloom' ? '🌸'
                      : '🍎'}
            </span>
            <div>
              <strong>{gatewayProgress?.stageKey ? t(`profileGateway.stage.${gatewayProgress.stageKey}`) : 'बीज'}</strong>
              <p>{gatewayProgress?.ready ? 'Growth is ready for human review.' : 'Growth is still unfolding.'}</p>
            </div>
          </div>
          <div className="sangati-dimension-strip">
            {VEDIC_DIMENSIONS.map((dimension) => (
              <span key={dimension.id} className="sangati-dimension-chip">
                <span>{dimension.icon}</span>
                <span>{dimension.label}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <header className="sangati-hero">
        <div>
          <div className="sangati-eyebrow">{t('sangati.eyebrow')}</div>
          <h1>{t('sangati.title')}</h1>
          <p>{t('sangati.subtitle')}</p>
        </div>
        <div className="sangati-hero-actions">
          <button className="sangati-btn sangati-btn--ghost" onClick={onNavigateHome}>
            {t('sangati.button.home')}
          </button>
          <button className="sangati-btn sangati-btn--ghost" onClick={onNavigateTutor}>
            {t('sangati.button.tutor')}
          </button>
          <button className="sangati-btn sangati-btn--primary" onClick={handleCreateGroup} disabled={!profile}>
            {profile ? t('sangati.button.create') : t('sangati.button.signIn')}
          </button>
        </div>
      </header>

      <SangatiRoadmap onNavigate={onNavigate} />

      {toast && <div className="sangati-toast">{toast}</div>}
      {error && <div className="sangati-error">{error}</div>}

      <section className="sangati-panel sangati-filters">
        <div className="sangati-panel-title">{t('sangati.filter.title')}</div>
        <div className="sangati-filter-grid">
          <label>
            <span>{t('sangati.filter.role')}</span>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}>
              <option value="all">{t('sangati.filter.all')}</option>
              <option value="learner">{t('sangati.filter.learner')}</option>
              <option value="knowledge_provider">{t('sangati.filter.provider')}</option>
              <option value="both">{t('sangati.filter.both')}</option>
            </select>
          </label>
          <label>
            <span>{t('sangati.filter.pincode')}</span>
            <input value={pincodeFilter} onChange={(e) => setPincodeFilter(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder={t('sangati.filter.placeholderPincode')} />
          </label>
          <label>
            <span>{t('sangati.filter.minAge')}</span>
            <input value={minAge} onChange={(e) => setMinAge(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))} placeholder={t('sangati.filter.placeholderMinAge')} />
          </label>
          <label>
            <span>{t('sangati.filter.maxAge')}</span>
            <input value={maxAge} onChange={(e) => setMaxAge(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))} placeholder={t('sangati.filter.placeholderMaxAge')} />
          </label>
          <label>
            <span>{t('sangati.filter.minRating')}</span>
            <input value={minRating} onChange={(e) => setMinRating(e.target.value.replace(/[^0-9.]/g, '').slice(0, 3))} placeholder={t('sangati.filter.placeholderMinRating')} />
          </label>
        </div>
      </section>

      <div className="sangati-layout">
        <aside className="sangati-sidebar">
          <section className="sangati-panel">
            <div className="sangati-panel-title">{t('sangati.group.title')}</div>
            {group ? (
              <>
                <div className="sangati-group-meta">
                  <strong>{group.name}</strong>
                  <span>{t('sangati.group.members', { count: visibleGroupMembers.length })}</span>
                </div>
                {visibleGroupMembers.length === 0 ? (
                  <p className="sangati-empty">{t('sangati.group.empty')}</p>
                ) : (
                  <div className="sangati-group-list">
                    {visibleGroupMembers.map((member) => (
                      <div key={member.uid} className="sangati-group-chip">
                        <span>{member.alias || t('sangati.unknown')}</span>
                        {member.uid !== group.ownerUid && (
                          <button onClick={() => handleRemoveMember(member.uid)} title={t('sangati.button.remove')}>✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="sangati-group-actions">
                  <button
                    className="sangati-btn sangati-btn--primary sangati-btn--block"
                    onClick={handleOpenGroupChat}
                    disabled={!profile || visibleGroupMembers.length < 2}
                  >
                    {groupThreadExists ? t('sangati.chat.openGroup') : t('sangati.chat.createGroup')}
                  </button>
                </div>
              </>
            ) : (
              <p className="sangati-empty">{t('sangati.group.none')}</p>
            )}
          </section>

          <section className="sangati-panel">
            <div className="sangati-panel-title">{t('sangati.assistant.title')}</div>
            <p className="sangati-assistant-lead">{t('sangati.assistant.subtitle')}</p>
            <div className="sangati-assistant-log">
              {assistantLog.map((entry, index) => (
                <div
                  key={`${entry.role}-${index}`}
                  className={`sangati-assistant-bubble sangati-assistant-bubble--${entry.role}`}
                >
                  {entry.text}
                </div>
              ))}
              {assistantLoading && (
                <div className="sangati-assistant-bubble sangati-assistant-bubble--assistant">
                  {t('sangati.assistant.loading')}
                </div>
              )}
            </div>
            {assistantProvider && (
              <div className="sangati-assistant-provider">
                {t('sangati.assistant.provider', { provider: assistantProvider })}
              </div>
            )}
            {assistantError && <div className="sangati-error">{assistantError}</div>}
            <form className="sangati-assistant-form" onSubmit={handleAssistantSubmit}>
              <textarea
                value={assistantInput}
                onChange={(e) => setAssistantInput(e.target.value)}
                placeholder={t('sangati.assistant.placeholder')}
                rows={4}
              />
              <button className="sangati-btn sangati-btn--primary" type="submit" disabled={assistantLoading}>
                {assistantLoading ? t('sangati.assistant.loading') : t('sangati.assistant.send')}
              </button>
            </form>
            <div className="sangati-assistant-prompts">
              {assistantPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="sangati-assistant-prompt"
                  onClick={() => void sendAssistantPrompt(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </section>

          <section className="sangati-panel">
            <div className="sangati-panel-title">{t('sangati.handshake.title')}</div>
            <p className="sangati-assistant-lead">{t('sangati.handshake.subtitle')}</p>
            <div className="sangati-handshake-steps">
              <div className="sangati-chat-subtitle">{t('sangati.handshake.stepsTitle')}</div>
              <ol>
                <li>{t('sangati.handshake.step1')}</li>
                <li>{t('sangati.handshake.step2')}</li>
                <li>{t('sangati.handshake.step3')}</li>
                <li>{t('sangati.handshake.step4')}</li>
              </ol>
            </div>
            <form className="sangati-assistant-form" onSubmit={handleLookupHandshakeCode}>
              <label className="sangati-field">
                <span>{t('sangati.handshake.code')}</span>
                <input
                  value={handshakeLookupCode}
                  onChange={(e) => setHandshakeLookupCode(e.target.value)}
                  placeholder={t('sangati.handshake.codePlaceholder')}
                />
              </label>
              <button className="sangati-btn sangati-btn--primary" type="submit" disabled={handshakeLoading}>
                {handshakeLoading ? t('sangati.assistant.loading') : t('sangati.handshake.lookup')}
              </button>
            </form>
            {handshakeTarget && (
              <form className="sangati-assistant-form" onSubmit={handleCreateHandshake}>
                <div className="sangati-handshake-target">
                  {t('sangati.handshake.target', { name: handshakeTarget.alias })}
                </div>
                <div className="sangati-thread-card-meta">
                  {t('sangati.handshake.targetCode', { code: handshakeTarget.code })}
                </div>
                <label className="sangati-field">
                  <span>{t('sangati.handshake.email')}</span>
                  <input
                    value={handshakeInviteeEmail}
                    onChange={(e) => setHandshakeInviteeEmail(e.target.value)}
                    placeholder={t('sangati.handshake.emailPlaceholder')}
                  />
                </label>
                <button className="sangati-btn sangati-btn--primary" type="submit" disabled={handshakeLoading}>
                  {t('sangati.handshake.request')}
                </button>
              </form>
            )}
            {handshakeError && <div className="sangati-error">{handshakeError}</div>}
            {handshakeRequests.length > 0 && (
              <div className="sangati-handshake-list">
                <div className="sangati-chat-subtitle">{t('sangati.handshake.pending')}</div>
                {handshakeRequests.map((request) => (
                  <div key={request.code} className="sangati-handshake-card">
                    <div>
                      <strong>{request.target_alias}</strong>
                      <div className="sangati-thread-card-meta">
                        {request.code} · {request.status} · {request.requester_alias}
                      </div>
                      <div className="sangati-thread-card-meta">{request.invitee_email}</div>
                    </div>
                    {request.status === 'pending' && normalizeEmail(user?.email ?? '') === normalizeEmail(request.invitee_email) && (
                      <button
                        type="button"
                        className="sangati-btn sangati-btn--ghost sangati-btn--compact"
                        onClick={() => void handleApproveHandshake(request)}
                      >
                        {t('sangati.handshake.approve')}
                      </button>
                    )}
                    {request.status === 'approved' && (
                      <span className="sangati-handshake-approved">{t('sangati.handshake.approved')}</span>
                    )}
                    {request.status === 'approved' && profile?.uid === request.requester_uid && (
                      <button
                        type="button"
                        className="sangati-btn sangati-btn--ghost sangati-btn--compact"
                        onClick={() => handleAddMemberByUid(request.target_uid)}
                      >
                        {t('sangati.handshake.addToGroup')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>

        <main className="sangati-main">
          <section className="sangati-panel sangati-chat-panel">
            <div className="sangati-panel-title">{t('sangati.chat.title')}</div>
            <div className="sangati-chat-shell">
              <aside className="sangati-thread-list">
                <div className="sangati-chat-subtitle">{t('sangati.chat.listTitle')}</div>
                {threadsLoading ? (
                  <p className="sangati-empty">{t('sangati.loading')}</p>
                ) : threads.length === 0 ? (
                  <p className="sangati-empty">{t('sangati.chat.noThreads')}</p>
                ) : (
                  threads.map((thread) => (
                    <button
                      key={thread.id}
                      type="button"
                      className={`sangati-thread-card ${thread.id === activeThreadId ? 'sangati-thread-card--active' : ''}`}
                      onClick={() => handleSelectThread(thread.id)}
                    >
                      <div className="sangati-thread-card-head">
                        <strong>{thread.title || (thread.type === 'group' ? t('sangati.chat.groupFallback') : t('sangati.chat.dmFallback'))}</strong>
                        <span>{thread.type === 'group' ? t('sangati.chat.groupBadge') : t('sangati.chat.dmBadge')}</span>
                      </div>
                      <div className="sangati-thread-card-meta">
                        {t('sangati.chat.participantCount', { count: thread.participant_count })}
                        {' · '}
                        {t('sangati.chat.messageCount', { count: thread.message_count })}
                      </div>
                      <div className="sangati-thread-card-preview">
                        {thread.last_message_preview || t('sangati.chat.noMessages')}
                      </div>
                      <div className="sangati-thread-card-time">
                        {thread.last_message_at ? formatTimestamp(thread.last_message_at) : ''}
                      </div>
                    </button>
                  ))
                )}
                {threadsError && <div className="sangati-error">{threadsError}</div>}
              </aside>

              <section className="sangati-thread-detail">
                <div className="sangati-thread-detail-head">
                  <div>
                    <div className="sangati-chat-subtitle">{t('sangati.chat.detailTitle')}</div>
                    <h3>{activeThread?.thread.title || t('sangati.chat.selectTitle')}</h3>
                    <p className="sangati-thread-detail-meta">
                      {activeThread ? (
                        <>
                          {activeThread.thread.type === 'group' ? t('sangati.chat.groupBadge') : t('sangati.chat.dmBadge')}
                          {' · '}
                          {activeThread.thread.participant_count} {t('sangati.chat.people')}
                          {' · '}
                          {activeThreadParticipants.join(' · ')}
                        </>
                      ) : (
                        t('sangati.chat.selectHint')
                      )}
                    </p>
                  </div>
                  {group && group.memberUids.length > 1 && (
                    <button
                      type="button"
                      className="sangati-btn sangati-btn--ghost sangati-btn--compact"
                      onClick={handleOpenGroupChat}
                    >
                      {t('sangati.chat.openGroup')}
                    </button>
                  )}
                </div>

                {activeThreadLoading ? (
                  <p className="sangati-empty">{t('sangati.loading')}</p>
                ) : activeThread ? (
                  <>
                    <div className="sangati-message-list">
                      {activeThread.messages.length === 0 ? (
                        <p className="sangati-empty">{t('sangati.chat.noMessages')}</p>
                      ) : (
                        activeThread.messages.map((message: SangatiMessage) => {
                          const mine = message.sender_uid === profile?.uid;
                          return (
                            <article key={message.id} className={`sangati-message ${mine ? 'sangati-message--mine' : ''}`}>
                              <div className="sangati-message-head">
                                <strong>{mine ? t('sangati.chat.you') : (memberLookup.get(message.sender_uid)?.alias || message.sender_alias || t('sangati.unknownMember'))}</strong>
                                <span>{formatTimestamp(message.created_at)}</span>
                              </div>
                              <p>{message.body}</p>
                            </article>
                          );
                        })
                      )}
                    </div>

                    <form className="sangati-compose" onSubmit={handleSendMessage}>
                      <textarea
                        value={composer}
                        onChange={(e) => setComposer(e.target.value)}
                        placeholder={t('sangati.chat.placeholder')}
                        rows={4}
                      />
                      <div className="sangati-compose-footer">
                        <span className="sangati-compose-hint">{t('sangati.chat.composeHint')}</span>
                        <button className="sangati-btn sangati-btn--primary" type="submit">
                          {t('sangati.chat.send')}
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <p className="sangati-empty">{t('sangati.chat.selectHint')}</p>
                )}
                {activeThreadError && <div className="sangati-error">{activeThreadError}</div>}
              </section>
            </div>
          </section>

          <section className="sangati-panel sangati-members-panel">
            <div className="sangati-panel-title">{t('sangati.list.title')}</div>
            <div className="sangati-count">
              {loading ? t('sangati.loading') : t('sangati.list.count', { count: visibleMembers.length })}
            </div>
            {loading ? (
              <p className="sangati-empty">{t('sangati.loading')}</p>
            ) : (
              <div className="sangati-member-list">
                {visibleMembers.map((member) => {
                  const initials = (member.alias || '??').slice(0, 2).toUpperCase();
                  const inGroup = !!group && group.memberUids.includes(member.uid);
                  const isSelf = member.uid === profile?.uid;
                  return (
                    <article key={member.uid} className={`sangati-member-card ${isSelf ? 'sangati-member-card--self' : ''}`}>
                      <div className="sangati-avatar" style={{ background: `hsl(${uidToHue(member.uid)},55%,52%)` }}>{initials}</div>
                      <div className="sangati-member-body">
                        <div className="sangati-member-head">
                          <h3>{member.alias || <em>{t('sangati.unknownMember')}</em>}</h3>
                          <span>{roleLabel(member.profileKind, t)}</span>
                        </div>
                        <div className="sangati-member-meta">
                          {member.age ?? '—'} {t('sangati.meta.years')} · {t('sangati.meta.pincode')} {member.pincode || '—'} · {t('sangati.meta.rating')} {typeof member.rating === 'number' ? member.rating.toFixed(1) : '—'}
                        </div>
                        <div className="sangati-member-meta">
                          {t('sangati.meta.subjects')}: {Array.isArray(member.studyAreas) && member.studyAreas.length ? member.studyAreas.join(' · ') : '—'}
                        </div>
                        <div className="sangati-member-meta">
                          {t('sangati.meta.qualification')}: {member.qualification || '—'} · {t('sangati.meta.time')}: {member.timeZone || '—'}
                        </div>
                        <div className="sangati-member-code">
                          <span>{t('sangati.handshake.memberCode', { code: member.uid })}</span>
                          <button type="button" onClick={() => handleUseMemberCode(member)}>
                            {t('sangati.handshake.useCode')}
                          </button>
                        </div>
                      </div>
                      <div className="sangati-member-actions">
                        {!isSelf && (
                          <button
                            className={`sangati-member-btn ${inGroup ? 'sangati-member-btn--active' : ''}`}
                            onClick={() => handleAddMember(member)}
                            type="button"
                          >
                            {inGroup ? t('sangati.button.inGroup') : t('sangati.button.add')}
                          </button>
                        )}
                        {inGroup && !isSelf && (
                          <button
                            className="sangati-member-btn sangati-member-btn--secondary"
                            type="button"
                            onClick={() => void handleOpenDirectMessage(member)}
                          >
                            {t('sangati.chat.dmAction')}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
